import { EntryFields } from 'contentful'
import { ButtonLink, Location } from '@sh24/ui-components'
import InlineLink from '../../InlineLink/inline-link'
import documentToReactComponents from '../../../utils/document-to-react-components'
import useTranslations from '../../../utils/use-translations'
import replaceTranslationTokens from '../../../utils/replace-translation-tokens'

interface OpeningTimes {
  title?: string
  monday?: string
  tuesday?: string
  wednesday?: string
  thursday?: string
  friday?: string
  saturday?: string
  sunday?: string
  info?: EntryFields.RichText
}

interface Location {
  name: string
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  postcode?: string | null
  phoneNumber?: string | null
  websiteUrl?: string | null
  googleMapsUrl?: string | null
  what3words?: string | null
  openingTimes?: OpeningTimes[] | null
  specialFeatures?: string[] | null
  services?: string[] | null
  additionalInfo?: EntryFields.RichText | null
}

const LocationModule = ({
  name,
  addressLine1 = null,
  addressLine2 = null,
  city = null,
  postcode = null,
  phoneNumber = null,
  websiteUrl = null,
  googleMapsUrl = null,
  what3words = null,
  openingTimes = null,
  specialFeatures = null,
  services = null,
  additionalInfo = undefined,
}: Location) => {
  const translations = useTranslations()
  const address = [
    addressLine1,
    addressLine2,
    city,
    postcode,
  ].filter((line) => !!line).join(', ')

  const phoneCtaText = replaceTranslationTokens({
    translations,
    translationKey: 'location.phoneCta',
    values: [phoneNumber],
  })

  const websiteCtaText = replaceTranslationTokens({
    translations,
    translationKey: 'location.websiteCta',
    values: [name],
  })

  const phoneNumberCta = phoneNumber ? (
    <ButtonLink variation="text">
      <InlineLink text={phoneCtaText} url={`tel:${phoneNumber}`} />
    </ButtonLink>
  ) : null

  const websiteCta = websiteUrl ? (
    <ButtonLink variation="text">
      <InlineLink
        text={websiteCtaText}
        url={websiteUrl}
        iconName="arrow-right"
        noFollow
        newTab
      />
    </ButtonLink>
  ) : null

  const googleMapsCta = googleMapsUrl ? (
    <ButtonLink variation="text">
      <InlineLink
        text={translations['location.googleMapsCta']}
        url={googleMapsUrl}
        iconName="arrow-right"
        noFollow
        newTab
      />
    </ButtonLink>
  ) : null

  const processedOpeningTimes = openingTimes?.map((ot) => ({
    ...ot,
    info: ot.info ? documentToReactComponents(ot.info) : null,
  }))

  return (
    <div className="row row-centered">
      <div className="col col-12 col-md-6">
        <div>
          <Location
            name={name}
            address={address}
            accordionTitle={translations['location.accordionTitle']}
            phoneNumberCta={phoneNumberCta}
            websiteCta={websiteCta}
            googleMapsCta={googleMapsCta}
            what3words={what3words}
            openingTimes={processedOpeningTimes}
            specialFeatures={specialFeatures}
            servicesHeading={translations['location.services']}
            services={services}
            additionalInfo={documentToReactComponents(additionalInfo)}
          />
        </div>
      </div>
    </div>
  )
}

export default LocationModule
